<template>
  <div class="app-page virtual-chat-page">
    <!-- ===== 没有虚拟恋人：显示设置页面 ===== -->
    <template v-if="!hasCharacter">
      <div class="settings-page">
        <div class="settings-card">
          <!-- 装饰背景 -->
          <div class="decoration-circle decoration-circle--top-left"></div>
          <div class="decoration-circle decoration-circle--bottom-right"></div>

          <!-- Banner -->
          <div class="settings-banner">
            <div class="settings-banner-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <div class="settings-banner-title">设定你的虚拟恋人</div>
            <p class="settings-banner-desc">在这里定义你心中的TA，每一个细节都将决定你们的互动体验</p>
          </div>

          <div class="settings-body">
            <CharacterForm
              ref="characterFormRef"
              :form-data="formData"
              :submit-loading="submitLoading"
              :character-id="characterId"
              @submit="handleSubmit"
              @reset="resetForm"
              @avatar-click="handleAvatarClick"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- ===== 有虚拟恋人：显示聊天页面 ===== -->
    <template v-else>
      <div class="chat-layout">
        <!-- 聊天框 -->
        <div class="chat-box">
          <!-- 头部 -->
          <div class="chat-header">
            <div class="chat-header-info">
              <div class="chat-header-avatar-wrapper">
                <img :src="virtualAvatar" class="chat-header-avatar" alt="">
                <span class="virtual-status-dot lg"></span>
              </div>
              <div class="chat-header-text">
                <span class="target-name">{{ virtualNickname }}</span>
                <span class="target-status">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="#8B7CF7"><circle cx="12" cy="12" r="12"/></svg>
                  AI在线
                </span>
              </div>
            </div>
            <router-link to="/virtual" class="settings-gear-btn" title="虚拟恋人设置">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </router-link>
          </div>

          <!-- 消息列表 -->
          <div
            class="message-list"
            ref="messageList"
            @scroll="handleScroll"
          >
            <div v-if="isLoadingMore" class="loading-more">
              <div class="loading-spinner"></div>
              <span>加载更多消息...</span>
            </div>

            <div
              v-for="msg in messageHistory"
              :key="msg.id || msg.tempId"
              class="message-item"
              :class="{ 'sent': msg.senderId === currentUserId, 'received': msg.senderId !== currentUserId }"
            >
              <div class="msg-avatar-wrapper">
                <img
                  :src="msg.senderId === currentUserId ? myAvatar : virtualAvatar"
                  class="msg-avatar"
                  alt="发送者头像"
                >
              </div>
              <div class="msg-content">
                <div class="msg-text" v-if="msg.type === 1">{{ msg.content }}</div>
                <div class="msg-image" v-if="msg.type === 2">
                  <img :src="msg.content" class="message-image" alt="图片消息" @click="previewImage(msg.content)">
                </div>
                <div class="msg-file" v-if="msg.type === 4">
                  <a :href="msg.content" target="_blank" class="file-link">
                    <svg class="file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                    <span class="file-name">{{ msg.fileName }}</span>
                  </a>
                </div>
                <div class="msg-time">{{ formatTime(msg.sendTime) }}</div>
              </div>
            </div>
          </div>

          <button
            class="scroll-to-bottom-btn"
            @click="scrollToBottom"
            v-if="showScrollToBottomBtn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </button>

          <!-- 输入区域 -->
          <div class="input-area">
            <label class="file-upload-btn" for="file-upload" title="上传文件">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
              <input
                id="file-upload"
                type="file"
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                @change="handleFileSelect"
                hidden
              >
            </label>

            <div v-if="selectedFile" class="file-preview">
              <img v-if="isImageFile(selectedFile)" :src="filePreviewUrl" class="image-preview" alt="预览图片">
              <div v-else class="file-info">
                <svg class="file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span class="file-name">{{ selectedFile.name }}</span>
                <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
              </div>
              <button class="remove-file" @click="clearSelectedFile">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <textarea
              v-model="inputContent"
              placeholder="输入消息..."
              class="message-input"
              @keydown.enter.exact="sendMessage"
              @keydown.enter.shift="() => inputContent += '\n'"
              :disabled="isSending"
            ></textarea>

            <button class="send-btn" @click="sendMessage" :disabled="isSending || (!inputContent.trim() && !selectedFile)">
              <span v-if="isSending">
                <div class="btn-spinner"></div>
              </span>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- 头像上传弹窗 -->
    <el-dialog
      title="更换头像"
      v-model="avatarDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :append-to-body="true"
      class="avatar-dialog"
    >
      <el-upload
        class="avatar-uploader"
        action=""
        :show-file-list="false"
        :on-change="handleFileChange"
        :before-upload="beforeAvatarUpload"
        accept="image/jpeg,image/png"
      >
        <div v-if="tempAvatarUrl" class="avatar-preview-wrapper">
          <img :src="tempAvatarUrl" class="avatar-preview-img">
        </div>
        <div v-else class="upload-placeholder">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.5"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
          <span>选择图片</span>
        </div>
        <div class="upload-hint">支持JPG、PNG格式，大小不超过5MB</div>
      </el-upload>

      <template #footer>
        <el-button @click="avatarDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAvatarChange" :loading="uploading">确认更换</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import socketService from '@/api/socket'
import VirtualCharacterApi from '@/api/virtual'
import CharacterForm from './CharacterForm.vue'

// --- 用户信息 ---
const storedUserId = localStorage.getItem('userId')
const currentUserId = ref(storedUserId ? Number(storedUserId) : 1)
const myAvatar = ref('https://picsum.photos/id/237/200')

// --- 虚拟对象信息 ---
const virtualUserId = ref(-1)
const virtualNickname = ref('加载中...')
const virtualAvatar = ref('https://picsum.photos/id/1025/200')
const characterId = ref(null)
const characterFormRef = ref(null)

const hasCharacter = computed(() => !!characterId.value)

// --- 聊天状态 ---
const messageHistory = ref([])
const inputContent = ref('')
const isSending = ref(false)
const showScrollToBottomBtn = ref(false)
const lastMessageContent = ref('')
const lastMessageTime = ref(null)

// --- 分页加载 ---
const messageList = ref(null)
const isLoadingMore = ref(false)
const pageNum = ref(1)
const pageSize = ref(30)
const hasMore = ref(true)
const initialLoadCompleted = ref(false)

// --- 文件相关 ---
const selectedFile = ref(null)
const filePreviewUrl = ref('')

// --- 设置表单 ---
const submitLoading = ref(false)
const initFormState = {
  name: '',
  gender: 2,
  age: 23,
  avatar: '',
  personality: '',
  hobbies: '',
  job: '',
  introduction: ''
}
const formData = reactive({ ...initFormState })
// --- 头像上传 ---
const avatarDialogVisible = ref(false)
const tempAvatarUrl = ref('')
const avatarSelectedFile = ref(null)
const uploading = ref(false)

// ===================== 获取虚拟对象信息 =====================

const fetchVirtualCharacter = async () => {
  try {
    const response = await VirtualCharacterApi.getMyCharacter()
    if (response.data && response.data.code == 200) {
      const data = response.data.data
      if (data) {
        virtualNickname.value = data.name || '虚拟对象'
        virtualAvatar.value = data.avatar || 'https://picsum.photos/id/1025/200'
        characterId.value = data.id
        Object.assign(formData, data)
      } else {
        characterId.value = null
      }
    }
  } catch (err) {
    console.error('获取虚拟对象信息失败:', err)
    characterId.value = null
  }
}

// ===================== 设置表单逻辑 =====================

const handleSubmit = async () => {
  if (!characterFormRef.value) return
  const formComponent = characterFormRef.value
  if (!formComponent.validate) {
    // 直接提交
    await doSubmit()
    return
  }
  formComponent.validate(async (valid) => {
    if (valid) await doSubmit()
  })
}

const doSubmit = async () => {
  submitLoading.value = true
  try {
    if (characterId.value) {
      const data = { ...formData, id: characterId.value }
      await VirtualCharacterApi.updateCharacter(data)
      ElMessage.success('更新成功！')
    } else {
      const res = await VirtualCharacterApi.createCharacter(formData)
      ElMessage.success('创建成功！')
      if (res.data && res.data.data && res.data.data.id) {
        characterId.value = res.data.data.id
      }
    }
    await fetchVirtualCharacter()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '操作失败，请稍后重试')
  } finally {
    submitLoading.value = false
  }
}

const resetForm = () => {
  if (characterFormRef.value && characterFormRef.value.resetFields) {
    characterFormRef.value.resetFields()
  }
}

// ===================== 头像上传逻辑 =====================

const handleAvatarClick = () => {
  ElMessageBox.confirm('是否要更换TA的头像？', '确认更换', {
    confirmButtonText: '是',
    cancelButtonText: '否',
    type: 'warning'
  }).then(() => {
    avatarDialogVisible.value = true
    tempAvatarUrl.value = ''
    avatarSelectedFile.value = null
  }).catch(() => {})
}

const handleFileChange = (file) => {
  avatarSelectedFile.value = file.raw
  const reader = new FileReader()
  reader.onload = (e) => { tempAvatarUrl.value = e.target.result }
  reader.readAsDataURL(file.raw)
}

const beforeAvatarUpload = (file) => {
  const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
  const isLt5M = file.size / 1024 / 1024 < 5
  if (!isJPG) ElMessage.error('只能上传JPG/PNG格式的图片!')
  if (!isLt5M) ElMessage.error('图片大小不能超过5MB!')
  return isJPG && isLt5M
}

const confirmAvatarChange = async () => {
  if (!avatarSelectedFile.value) {
    ElMessage.warning('请选择图片')
    return
  }
  try {
    uploading.value = true
    const formDataObj = new FormData()
    formDataObj.append('file', avatarSelectedFile.value)
    formDataObj.append('fileType', 2)
    const response = await axios.post('/message/api/v1/files/upload', formDataObj, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    if (response.data.success && response.data.data?.accessUrl) {
      formData.avatar = response.data.data.accessUrl
      ElMessage.success('头像更新成功')
      avatarDialogVisible.value = false
    } else {
      ElMessage.error('头像上传失败')
    }
  } catch (err) {
    console.error('更换头像失败:', err)
    ElMessage.error('更换头像失败，请稍后重试')
  } finally {
    uploading.value = false
  }
}

// ===================== 历史消息逻辑 =====================

const fetchMessageHistory = async (reset = false) => {
  if (reset) {
    pageNum.value = 1
    messageHistory.value = []
    hasMore.value = true
    initialLoadCompleted.value = false
    isLoadingMore.value = false
  }
  if (!hasMore.value || isLoadingMore.value || (reset === false && !initialLoadCompleted.value)) {
    return
  }
  isLoadingMore.value = true
  try {
    const res = await axios.get('/message/api/v1/messages/virtual_history', {
      params: {
        userId: currentUserId.value,
        pageNum: pageNum.value,
        pageSize: pageSize.value,
        sort: 'sendTime,desc'
      }
    })
    if (res.data.success && Array.isArray(res.data.data)) {
      const list = res.data.data
      const newRecords = [...list].sort((a, b) => new Date(a.sendTime.replace(/-/g, '/')) - new Date(b.sendTime.replace(/-/g, '/')))
      messageHistory.value = reset ? newRecords : [...newRecords, ...messageHistory.value]
      hasMore.value = list.length >= pageSize.value
      if (hasMore.value) pageNum.value++
    }
  } catch (error) {
    console.error('获取虚拟聊天历史消息失败:', error)
  } finally {
    isLoadingMore.value = false
  }
}

// ===================== WebSocket 逻辑 =====================

const initWebSocket = () => {
  socketService.off('open')
  socketService.off('message')
  socketService.off('error')
  socketService.off('maxReconnect')

  socketService.on('open', () => {
    console.log('WebSocket连接已建立 (虚拟聊天)')
    if (currentUserId.value) {
      socketService.send({
        type: 'connect',
        userId: currentUserId.value
      }).catch(err => console.error('发送连接信息失败:', err))
    }
  })

  socketService.on('message', handleReceivedMessage)

  socketService.on('error', (error) => {
    console.error('WebSocket错误:', error)
  })

  socketService.on('maxReconnect', (maxAttempts) => {
    ElMessage.error(`已达到最大${maxAttempts}次重连尝试`)
  })

  if (currentUserId.value) {
    socketService.connect({ userId: currentUserId.value })
  }
}

const handleReceivedMessage = (message) => {
  if (message.type === 5) return
  const isRelated = (message.receiverId === currentUserId.value && message.senderId === virtualUserId.value) ||
    (message.senderId === currentUserId.value && message.receiverId === virtualUserId.value)
  if (isRelated) {
    const exists = messageHistory.value.find(m =>
      (m.id && m.id === message.id) || (m.tempId && m.tempId === message.tempId)
    )
    if (!exists) {
      messageHistory.value.push(message)
      updateLastMessage(message)
      scrollToBottom()
    }
  }
}

// ===================== 业务逻辑 =====================

const updateLastMessage = (msg) => {
  lastMessageContent.value = msg.content
  lastMessageTime.value = msg.sendTime
}

const sendMessage = async () => {
  if ((!inputContent.value.trim() && !selectedFile.value) || isSending.value) return
  isSending.value = true
  try {
    let fileData = null
    if (selectedFile.value) {
      fileData = await uploadFile(selectedFile.value)
      if (!fileData) {
        ElMessage.error('文件上传失败')
        return
      }
    }
    const tempId = Date.now() + Math.random()
    const messagePayload = {
      tempId,
      senderId: currentUserId.value,
      receiverId: virtualUserId.value,
      content: fileData ? fileData.accessUrl : inputContent.value.trim(),
      type: selectedFile.value ? (isImageFile(selectedFile.value) ? 2 : 4) : 1,
      status: 0,
      fileName: selectedFile.value ? selectedFile.value.name : null,
      sendTime: new Date().toISOString()
    }
    messageHistory.value.push(messagePayload)
    updateLastMessage(messagePayload)
    scrollToBottom()
    inputContent.value = ''
    clearSelectedFile()
    await socketService.send(messagePayload)
  } catch (error) {
    console.error('发送异常:', error)
    ElMessage.error('网络错误或发送失败')
  } finally {
    isSending.value = false
  }
}

const uploadFile = async (file) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    const res = await axios.post('/message-service/api/v1/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return res.data.success ? res.data.data : null
  } catch (error) {
    console.error('文件上传错误:', error)
    return null
  }
}

const handleFileSelect = (e) => {
  const file = e.target.files[0]
  if (file) {
    selectedFile.value = file
    if (isImageFile(file)) {
      const reader = new FileReader()
      reader.onload = (ev) => filePreviewUrl.value = ev.target.result
      reader.readAsDataURL(file)
    }
  }
}

const isImageFile = (file) => file && file.type.startsWith('image/')
const clearSelectedFile = () => {
  selectedFile.value = null
  filePreviewUrl.value = ''
  document.getElementById('file-upload').value = ''
}
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
const previewImage = (url) => window.open(url, '_blank')

const handleScroll = () => {
  if (!messageList.value || !initialLoadCompleted.value) return
  const { scrollTop, clientHeight, scrollHeight } = messageList.value
  showScrollToBottomBtn.value = scrollTop + clientHeight < scrollHeight - 300
  if (scrollTop < 50 && hasMore.value && !isLoadingMore.value) {
    fetchMessageHistory(false)
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messageList.value) {
      messageList.value.scrollTo({ top: messageList.value.scrollHeight, behavior: 'smooth' })
      showScrollToBottomBtn.value = false
    }
  })
}

const formatTime = (time) => {
  if (!time) return ''
  const date = new Date(time)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// --- 生命周期 ---
onMounted(() => {
  fetchVirtualCharacter().then(() => {
    if (hasCharacter.value) {
      initWebSocket()
      fetchMessageHistory(true)
    }
  })
})

watch(messageHistory, (newVal, oldVal) => {
  if (oldVal.length === 0 && newVal.length > 0) {
    scrollToBottom()
    setTimeout(() => { initialLoadCompleted.value = true }, 500)
  } else if (newVal.length > oldVal.length) {
    if (messageList.value && messageList.value.scrollTop + messageList.value.clientHeight >= messageList.value.scrollHeight - 50) {
      scrollToBottom()
    } else {
      showScrollToBottomBtn.value = true
    }
  }
})

onUnmounted(() => {
  socketService.off('message', handleReceivedMessage)
  socketService.close()
})
</script>

<style scoped>
.virtual-chat-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ========================================
   设置页面（无虚拟恋人时）
   ======================================== */
.settings-page {
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 24px 0;
  overflow-y: auto;
}

.settings-card {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 800px;
  align-self: flex-start;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 126, 126, 0.08) 0%, rgba(255, 126, 126, 0.03) 100%);
  z-index: 0;
  pointer-events: none;
}
.decoration-circle--top-left {
  width: 200px;
  height: 200px;
  top: -100px;
  left: -100px;
}
.decoration-circle--bottom-right {
  width: 300px;
  height: 300px;
  bottom: -150px;
  right: -150px;
}

.settings-banner {
  padding: 28px 24px;
  background: var(--app-gradient-primary);
  color: white;
  text-align: center;
  position: relative;
  z-index: 1;
  overflow: hidden;
}
.settings-banner::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  pointer-events: none;
}
.settings-banner-icon { margin-bottom: 8px; opacity: 0.9; }
.settings-banner-title {
  font-size: 1.375rem;
  font-weight: 700;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}
.settings-banner-desc {
  margin: 0 auto;
  font-size: 0.875rem;
  opacity: 0.85;
  line-height: 1.6;
  max-width: 28rem;
  position: relative;
  z-index: 1;
}

.settings-body {
  padding: 24px;
  position: relative;
  z-index: 1;
}

/* ========================================
   聊天模式
   ======================================== */
.chat-layout {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.chat-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  margin-top: 16px;
  overflow: hidden;
}

/* ── 聊天头部 ── */
.chat-header {
  padding: 12px 20px;
  border-bottom: 1px solid var(--app-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--app-bg-elevated);
  flex-shrink: 0;
}

.chat-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-header-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.chat-header-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}

.virtual-status-dot {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: linear-gradient(135deg, #A99BFF, #8B7CF7);
  border: 2px solid var(--app-bg-elevated);
}
.virtual-status-dot.lg {
  width: 16px;
  height: 16px;
}

.chat-header-text {
  display: flex;
  flex-direction: column;
}

.target-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

.target-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #8B7CF7;
}

/* ── 设置齿轮按钮 ── */
.settings-gear-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  text-decoration: none;
}
.settings-gear-btn:hover {
  background: var(--app-bg-soft);
  color: var(--app-text-secondary);
  transform: rotate(30deg);
}

/* ── 消息列表 ── */
.message-list {
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
  background: var(--app-bg-soft);
  scroll-behavior: smooth;
}

.message-list::-webkit-scrollbar { width: 5px; }
.message-list::-webkit-scrollbar-thumb { background: var(--app-border-light); border-radius: 3px; }

.loading-more {
  text-align: center;
  padding: 12px 0;
  color: var(--app-text-muted);
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--app-border-light);
  border-top-color: #8B7CF7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.scroll-to-bottom-btn {
  position: absolute;
  bottom: 80px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--app-bg-elevated);
  color: #8B7CF7;
  border: 1px solid var(--app-border-light);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--app-shadow-card);
  transition: all 0.2s;
  z-index: 5;
}

.scroll-to-bottom-btn:hover {
  background: linear-gradient(135deg, #8B7CF7, #A99BFF);
  color: white;
  border-color: transparent;
  transform: translateY(-2px);
  box-shadow: var(--app-shadow-card-hover);
}

/* ── 消息项 ── */
.message-item {
  margin-bottom: 16px;
  display: flex;
  max-width: 80%;
  animation: msgFadeIn 0.25s ease;
}

@keyframes msgFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.message-item.sent { margin-left: auto; flex-direction: row-reverse; }
.msg-avatar-wrapper { flex-shrink: 0; }
.msg-avatar { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; }
.message-item.received .msg-avatar-wrapper { margin-right: 10px; }
.message-item.sent .msg-avatar-wrapper { margin-left: 10px; }
.msg-content { max-width: calc(100% - 44px); }

.msg-text {
  padding: 10px 16px;
  border-radius: 18px;
  word-break: break-word;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.message-item.received .msg-text {
  background: var(--app-bg-elevated);
  color: var(--app-text-primary);
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.message-item.sent .msg-text {
  background: linear-gradient(135deg, #8B7CF7, #A99BFF);
  color: white;
  border-bottom-right-radius: 4px;
}

.msg-time {
  font-size: 0.6875rem;
  color: var(--app-text-muted);
  margin-top: 4px;
}
.message-item.received .msg-time { text-align: left; padding-left: 4px; }
.message-item.sent .msg-time { text-align: right; padding-right: 4px; }

.msg-image img {
  max-width: 220px;
  max-height: 220px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.msg-image img:hover { transform: scale(1.02); }

.msg-file .file-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--app-bg-elevated);
  border-radius: 12px;
  color: var(--app-text-primary);
  text-decoration: none;
  max-width: 260px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: all 0.2s;
  border: 1px solid var(--app-border-subtle);
}
.msg-file .file-link:hover {
  background: var(--app-bg-soft);
  border-color: rgba(108, 92, 231, 0.2);
}
.file-name {
  font-size: 0.8125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

/* ── 输入区域 ── */
.input-area {
  padding: 12px 16px;
  border-top: 1px solid var(--app-border-light);
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: var(--app-bg-elevated);
  flex-shrink: 0;
}

.file-upload-btn {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  transition: all 0.2s;
  flex-shrink: 0;
}
.file-upload-btn:hover { background: var(--app-bg-soft); color: #8B7CF7; }

.file-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  width: 100%;
  margin-bottom: 4px;
}

.image-preview {
  max-width: 60px;
  max-height: 60px;
  border-radius: 6px;
  object-fit: cover;
}

.file-info { display: flex; align-items: center; flex: 1; gap: 8px; }
.file-size { color: var(--app-text-muted); font-size: 0.75rem; }

.remove-file {
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e74c3c;
  transition: all 0.2s;
  flex-shrink: 0;
}
.remove-file:hover { background: rgba(231, 76, 60, 0.1); }

.message-input {
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 16px;
  border: 1px solid var(--app-border-light);
  border-radius: 20px;
  resize: none;
  outline: none;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: var(--app-bg-soft);
}
.message-input:focus {
  border-color: #8B7CF7;
  box-shadow: 0 0 0 3px rgba(108, 92, 231, 0.12);
  background: var(--app-bg-elevated);
}
.message-input::placeholder { color: var(--app-text-muted); }
.message-input:disabled { opacity: 0.6; cursor: not-allowed; }

.send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8B7CF7, #A99BFF);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}
.send-btn:hover { transform: scale(1.05); box-shadow: 0 4px 12px rgba(108, 92, 231, 0.35); }
.send-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; box-shadow: none; }

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ── 头像弹窗 ── */
.avatar-dialog :deep(.el-dialog__body) { padding: 20px 24px; }
.avatar-uploader { text-align: center; padding: 12px 0; }

.avatar-preview-wrapper { display: inline-block; }
.avatar-preview-img {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--app-border-light);
  display: block;
  margin: 0 auto;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 30px;
  border: 2px dashed var(--app-border-light);
  border-radius: var(--app-radius-md);
  cursor: pointer;
  transition: all 0.2s;
  color: var(--app-text-muted);
  font-size: 0.875rem;
}
.upload-placeholder:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: var(--app-color-primary-muted);
}
.upload-hint { color: var(--app-text-muted); font-size: 0.75rem; margin-top: 12px; }

/* ── 响应式 ── */
@media (max-width: 768px) {
  .settings-page { padding: 0; }
  .settings-body { padding: 16px; }
  .chat-box { border-radius: 0; border: none; margin-top: 0; }
  .message-list { padding: 12px 14px; }
  .message-item { max-width: 90%; }
  .scroll-to-bottom-btn { bottom: 70px; right: 12px; width: 36px; height: 36px; }
  .input-area { padding: 10px 12px; }
}
</style>
